import { BUTTON_SIZE, BUTTON_TYPE, COLORS, DSButton } from '@repo/design-system/index'
import { debounce } from 'lodash'
import { useState } from 'react'
import apis from '../../../modules/apis'
import { encryptPhoneNum } from '../../../modules/auth'
import { axiosPost } from '../../../modules/axiosHelper'
import { useCountdownTimer } from '../../../modules/hooks/common/useCountdownTimer'
import { s_auth, s_signup, s_trial } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import { DATA_REF_KEY } from '../../../modules/ids/e2e/student'
import { isKrPhone } from '../../../modules/validator'
import Input from '../../_legacy/Input'
import { useConfirmPopup } from '../../basic/popup/ConfirmPopup'

interface PhoneVerifySectionProps {
  phone: string
  phoneIntl: {
    label: string
    value: string
  }
  phoneVerified: boolean
  verified: boolean
  setVerified: (value: boolean) => void
}

const PhoneVerifySection = ({ phone, phoneIntl, phoneVerified, verified, setVerified }: PhoneVerifySectionProps) => {
  const { t, locale } = useTranslation()
  const [request, setRequest] = useState(false)
  const [verifyCode, setVerifyCode] = useState('')
  const enableCodeRequest = phone.length >= 4 && phoneVerified
  const { show, close } = useConfirmPopup()

  const handleVerifyText = (code) => {
    setVerifyCode(code)
  }
  const { countdown, start, isRunning } = useCountdownTimer({
    timer: 1000 * 60 * 3,
    onExpire: () => {
      setVerifyCode('')
      setRequest(false)
    },
  })

  const validatePhone = (phone, phoneIntl) => {
    if (!phone) {
      alert(t(s_trial.enter_your_phone_number))
      return false
    }

    if (phoneIntl.code && phoneIntl.code == '+82' && !isKrPhone(phone)) {
      alert(t(s_auth.phone_check))
      return false
    }

    return true
  }
  const displayModal = (message) => {
    show({
      id: 'confirm-modal',
      title: t(s_auth.send_verification),
      body: message,
      rightBtnLabel: t(s_auth.ok),
      leftBtn: false,
      rightBtn: true,
      onRightBtnClick: close('confirm-modal'),
      rightBtnDataRef: DATA_REF_KEY.btn_modal_verification,
    })
  }

  const requestPhoneVerify = async (e) => {
    e.preventDefault()

    if (!validatePhone(phone, phoneIntl)) {
      return
    }

    try {
      const response = await axiosPost(
        apis.profile.phoneCodeRequest(locale),
        {
          phone_number: encryptPhoneNum(`${phoneIntl.value}` + phone),
        },
        { shouldThrowError: true }
      )
      start()
      displayModal(response.message)
      setRequest(true)
      setVerifyCode('')
    } catch (error) {
      console.error('Error:', error)
      const errorMessage = error.response?.data?.error ?? error.message
      alert(errorMessage)
    }
  }
  const minutes = Math.floor(countdown / 60000)
  const seconds = (countdown % 60000) / 1000
  const timeStr = `${minutes.toFixed(0)}:${seconds < 10 ? '0' : ''}${seconds.toFixed(0)}`
  const submittable = isRunning && verifyCode.length >= 4

  const phoneCodeVerify = async (e) => {
    e.preventDefault()
    try {
      await axiosPost(
        apis.profile.phoneCodeAuthorized(locale),
        {
          phone_number: `${phoneIntl.value}` + phone,
          code: verifyCode,
        },
        { shouldThrowError: true }
      )
      setVerified(true)
    } catch (e) {
      alert(e.response.data?.error ?? e.message)
    }
  }

  return (
    <>
      {/* 인증 전, 요청 후 */}
      {!verified && request && (
        <div className="relative">
          <Input
            // {...verifyCode}
            name="verify"
            onChange={(e) => handleVerifyText(e.target.value)}
            type="text"
            autoComplete="verify"
            placeholder={t(s_signup.veri_number)}
            tabIndex={6}
            clx="mt-[8px]"
            style={{ padding: '16px', height: '52px' }}
            dataRef={DATA_REF_KEY.inp_verification_code}
          />
          <div className="text-h5 absolute right-0 top-0 mt-[15px] mr-[16px] font-medium text-purple-400">
            {timeStr}
          </div>
        </div>
      )}
      <div className="mt-[8px] flex justify-end">
        <div className="text-left">
          {/* 인증 완료 */}
          {verified ? (
            <DSButton size={BUTTON_SIZE.MD} disabled={true}>
              <DSButton.Label>{t(s_signup.verified_success)}</DSButton.Label>
            </DSButton>
          ) : request ? (
            <div className="flex items-center gap-x-[8px]">
              <DSButton
                disabled={!enableCodeRequest}
                type={BUTTON_TYPE.OUTLINE}
                customBorderColor={COLORS.PURPLE600}
                onClick={enableCodeRequest && debounce((e) => requestPhoneVerify(e), 500)}
                className="whitespace-nowrap"
              >
                <DSButton.Label customTxtColor={COLORS.PURPLE600}>
                  {t(s_auth.verification_code_re_request)}
                </DSButton.Label>
              </DSButton>
              <DSButton
                size={BUTTON_SIZE.MD}
                type={BUTTON_TYPE.SOLID}
                disabled={!submittable}
                onClick={submittable && phoneCodeVerify}
              >
                <DSButton.Label>{t(s_auth.submit_code)}</DSButton.Label>
              </DSButton>
            </div>
          ) : (
            // 인증 요청
            <DSButton
              size={BUTTON_SIZE.MD}
              type={BUTTON_TYPE.OUTLINE}
              customBorderColor={COLORS.PURPLE600}
              disabled={!enableCodeRequest}
              onClick={enableCodeRequest && debounce((e) => requestPhoneVerify(e), 500)}
            >
              <DSButton.Label customTxtColor={COLORS.PURPLE600}>{t(s_auth.verification_code_request)}</DSButton.Label>
            </DSButton>
          )}
        </div>
      </div>
    </>
  )
}

export default PhoneVerifySection
